import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect
} from "react";
// Fdr
import { onTriggerDeposit } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
// Utils
import { get } from "lodash";
import BetUtils from "@tvg/utils/betSelection";
import {
  getBettingInfo,
  simpleBetTypes
} from "@tvg/desktop-bet/src/utils/betUtils";
import { getCurrentProbableAmount } from "@tvg/desktop-bet/src/utils/getWPSProbableValue";
// Store
import { useDispatch, useSelector } from "react-redux";
import {
  setBetAmount,
  setHasUserSetAmount
} from "@tvg/desktop-bet/src/store/actions";
import { getBetType, getBetAmount } from "@tvg/desktop-bet/src/store/selectors";
import { toggleQuickDepositModal } from "@tvg/sh-lib-paws/redux/slices/quickDepositModalSlice";
// Components
import {
  IconButton,
  useWindowSize,
  useMediaQuery,
  breakpoints,
  useTheme
} from "@tvg/design-system";
import { getBalance, getIsLogged } from "@urp/store-selectors";
import { useLocation } from "react-router";
import {
  getIsBetSlipExpanded,
  getUserAcceptedTermsAndConditions,
  getIsUserVerified
} from "../../store/selectors";
import AnimatedContainer from "./components/AnimatedContainer";
import BetAmountSelector from "./components/BetAmountSelector";
import PlaceBetButton from "./components/PlaceBetButton";
import InsufficientFundsButton from "./components/InsufficientFundsButton";
import BetSelectionsList from "./components/BetSelectionsList";
import AnimatedExtendedSelectionsContainer from "./components/AnimatedExtendedSelectionsContainer";
import AnimatedSelectionsContainer from "./components/AnimatedSelectionsContainer";
import { BetTypeDescription } from "./components/BetTypeDescription";
import CustomAmountModal from "./components/CustomAmountModal";
import VerifyButton from "./components/VerifyButton";
import Alerts from "./components/Alerts";
import {
  BetSelectionsContainer,
  BetSlipContainer,
  BetSlipWrapper,
  ExtendedSelectionsContainer,
  LeftColumn,
  RightColumn,
  ScrollButtonsContainer,
  ScrollContainer
} from "./styled-components";
// GTMs
import {
  selectBetAmountGtmEvent,
  removeAllBetsGtmEvent,
  placeBetGtmEvent
} from "../../utils/gtm/betslip";
import { isFdr } from "../../utils/general";
// Types
import { BetSlipProps, ScrollDirection, ScrollActions } from "./types";
import useGeocomplyBetDisabled from "../ConfirmBetModal/hooks/useGeocomplyBetDisabled";

const BetSlip = ({
  race,
  races,
  onDelete,
  onPlaceBet,
  selections,
  isVisible,
  isLoading,
  betType,
  isBettingDisabled = false
}: BetSlipProps) => {
  const dispatch = useDispatch();
  const [isCustomAmountOpen, toggleCustomAmountModal] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const theme = useTheme();
  const [isExtendedSelections, setIsExtendedSelections] = useState(false);
  const isUserVerified = useSelector(getIsUserVerified);
  const userAcceptedTermsAndConditions = useSelector(
    getUserAcceptedTermsAndConditions
  );
  const selectedWagerType = useSelector(getBetType);
  const userBalance = useSelector(getBalance);
  const isLogged = useSelector(getIsLogged);
  const isBetSlipExpanded = useSelector(getIsBetSlipExpanded);
  const typeFromRace = BetUtils.getBetTypeFromRace(race, selectedWagerType);
  const wagerAmounts = typeFromRace?.wagerAmounts.map(String);
  const selectedAmount = useSelector(getBetAmount);
  const trackName = get(race, "track.trackName");
  const raceNumber = get(race, "raceNumber");
  const betTypeNames = BetUtils.getWagerTypesNames(typeFromRace);
  const legCount = typeFromRace?.legCount || 1;
  const betSelectionsContainerRef = useRef<HTMLDivElement>(null);
  const extendedBetSelectionsContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const betSlipWrapperRef = useRef<HTMLDivElement>(null);
  const [scrollActions, setScrollActions] = useState<ScrollActions>("none");
  const {
    isBetDisabled: isGeoComplyBetDisabled,
    isGeolocationErrorRetryable,
    isGeolocationError
  } = useGeocomplyBetDisabled();
  const isFDR = isFdr();
  const isAccountCompliant =
    !isFDR ||
    !isLogged ||
    (isUserVerified && userAcceptedTermsAndConditions && isLogged);

  const { width } = useWindowSize();
  const isTabletMinSm = useMediaQuery(breakpoints.tablet.min.sm);

  const betTypeCode = typeFromRace?.type.code;
  // legCount bigger them 1 is a pick bet
  const isExoticBet = !simpleBetTypes.includes(betTypeCode) && legCount === 1;

  const { fullSelections, totalAmount } = getBettingInfo(
    selections,
    selectedAmount,
    race,
    races,
    typeFromRace
  );

  const location = useLocation();

  const isMyBets = location.pathname.includes("my-bets");

  const onSelectAmount = useCallback((value: string | number) => {
    const amountSelected = typeof value === "number" ? value.toString() : value;
    selectBetAmountGtmEvent(amountSelected);
    dispatch(setBetAmount(+value));
    dispatch(setHasUserSetAmount(true));
  }, []);

  const approxPayout = useMemo(
    (): string | undefined =>
      getCurrentProbableAmount(
        get(race, "probables", []),
        selectedAmount,
        selections,
        get(typeFromRace, "specialGroup", false),
        typeFromRace
      ),
    [race, betType, selectedAmount, selections]
  );

  const remainingSelections = useMemo(
    () => selections.filter((selection) => selection.length === 0).length,
    [selections]
  );

  const onClickInsufficientFunds = useCallback(() => {
    if (isFDR) {
      onTriggerDeposit();
    } else {
      dispatch(toggleQuickDepositModal(true));
    }
  }, [isFDR]);

  const onDeleteSelectionsGtm = useCallback(() => {
    removeAllBetsGtmEvent();
  }, []);

  const onPlaceBetGtm = useCallback(
    (value: string | number) => {
      placeBetGtmEvent({
        raceNumber,
        trackName,
        betType,
        betAmount: Math.trunc(+value),
        selectionSource: isMyBets
          ? "repeat_bet_mybets"
          : "repeat_bet_race_card",
        raceType: race.type?.name
      });
    },
    [betType]
  );

  const onClickScroll = useCallback((scrollDirection: ScrollDirection) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft +=
        scrollDirection === "left" ? -100 : 100;

      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      let activeScrollActions: ScrollActions;

      if (scrollLeft === 0) {
        activeScrollActions = "right";
      } else {
        activeScrollActions =
          Math.floor(scrollWidth - clientWidth) === Math.ceil(scrollLeft)
            ? "left"
            : "both";
      }

      setScrollActions(activeScrollActions);
    }
  }, []);

  useEffect(() => {
    if (betSlipWrapperRef.current && isTabletMinSm) {
      const marginBetweenElements = 8;
      const betSlipElementsArea = 0.63;
      const maxWidth =
        betSlipWrapperRef.current.clientWidth * betSlipElementsArea;
      const minWidth =
        betSlipWrapperRef.current.clientWidth * betSlipElementsArea;
      const betSelectionChildren = betSelectionsContainerRef?.current?.children;
      const extendedBetSelectionsChildren =
        extendedBetSelectionsContainerRef?.current?.children;

      if (betSelectionChildren?.length) {
        let betsWidth = 0;
        for (let i = 0; i < betSelectionChildren.length; i++) {
          betsWidth +=
            betSelectionChildren[i].clientWidth + marginBetweenElements;
        }
        if (betsWidth > maxWidth) {
          setIsExtendedSelections(true);
        }
      }

      if (extendedBetSelectionsChildren?.length) {
        let betsWidth = 0;
        for (let i = 0; i < extendedBetSelectionsChildren.length; i++) {
          betsWidth +=
            extendedBetSelectionsChildren[i].clientWidth +
            marginBetweenElements;
        }
        if (betsWidth <= minWidth) {
          setIsExtendedSelections(false);
        }
      }

      setTimeout(() => {
        if (scrollContainerRef.current) {
          const { clientWidth, scrollWidth, scrollLeft } =
            scrollContainerRef.current;
          let activeScrollActions: ScrollActions = "none";

          if (scrollWidth > clientWidth) {
            const action =
              Math.floor(scrollWidth - clientWidth) === Math.ceil(scrollLeft)
                ? "left"
                : "both";

            activeScrollActions = scrollLeft === 0 ? "right" : action;
          }

          setScrollActions(activeScrollActions);
        }
      }, 300);
    } else if (!isTabletMinSm) {
      setIsExtendedSelections(true);
    }
  }, [
    selections,
    scrollContainerRef.current,
    betSlipWrapperRef,
    width,
    isTabletMinSm
  ]);

  const showVerifyButton = useMemo(
    () =>
      // 1st condition: Show verify account button if account is not compliant and there is no GeoLocation error
      // 2nd condition: Show verify location button if there is GeoLocation error and GeoLocation error is retryable
      ((!isAccountCompliant && !isGeolocationError) ||
        (isGeolocationError && isGeolocationErrorRetryable)) &&
      isLogged,
    [
      isAccountCompliant,
      isGeolocationError,
      isGeolocationErrorRetryable,
      isLogged
    ]
  );

  return (
    <>
      <CustomAmountModal
        isOpen={isCustomAmountOpen}
        toggleCustomAmountModal={toggleCustomAmountModal}
        setCustomAmount={setCustomAmount}
        onSelectAmount={onSelectAmount}
      />
      <AnimatedContainer isVisible={isVisible}>
        <Alerts
          race={race}
          isAccountCompliant={isAccountCompliant}
          isGeolocationError={isGeolocationError}
        />
        <BetSlipContainer isBetSlipExpanded={isBetSlipExpanded}>
          {selections && (
            <AnimatedExtendedSelectionsContainer
              isVisible={isExtendedSelections}
            >
              <ScrollContainer ref={scrollContainerRef}>
                <ExtendedSelectionsContainer
                  ref={extendedBetSelectionsContainerRef}
                >
                  {fullSelections && (
                    <BetSelectionsList
                      isExoticBet={isExoticBet}
                      selectionsList={fullSelections.map((runnerSelections) =>
                        runnerSelections.filter((runner) => !runner.scratched)
                      )}
                      betTypeNames={betTypeNames}
                      isLeg={legCount > 1}
                    />
                  )}
                </ExtendedSelectionsContainer>
              </ScrollContainer>
              <ScrollButtonsContainer>
                <IconButton
                  variant="secondary_dark"
                  size="s"
                  iconName="arrowLeft"
                  isDisabled={
                    scrollActions !== "left" && scrollActions !== "both"
                  }
                  onPress={() => onClickScroll("left")}
                  qaLabel="bet-slip-scroll-left"
                />
                <IconButton
                  variant="secondary_dark"
                  size="s"
                  iconName="arrowRight"
                  isDisabled={
                    scrollActions !== "right" && scrollActions !== "both"
                  }
                  onPress={() => onClickScroll("right")}
                  qaLabel="bet-slip-scroll-right"
                />
              </ScrollButtonsContainer>
            </AnimatedExtendedSelectionsContainer>
          )}
          <BetSlipWrapper
            theme={theme}
            hasRadiusTop={!isExtendedSelections}
            ref={betSlipWrapperRef}
          >
            <LeftColumn>
              <IconButton
                onPress={() => {
                  onDelete();
                  onDeleteSelectionsGtm();
                }}
                qaLabel="bet-slip-delete-button"
                variant="tertiary_dark"
                iconName="trash"
                mr={isExoticBet ? "space-1" : "space-3"}
                ml="space-1"
              />
              {(isExoticBet || legCount > 1) && (
                <BetTypeDescription
                  betTypeCode={typeFromRace?.group.code}
                  isKey={typeFromRace?.isKey}
                  isBox={typeFromRace?.isBox}
                  isWheel={typeFromRace?.isWheel}
                />
              )}
              {selections && (
                <AnimatedSelectionsContainer isVisible={!isExtendedSelections}>
                  <BetSelectionsContainer
                    numberOfCols={fullSelections.length}
                    ref={betSelectionsContainerRef}
                  >
                    {fullSelections && (
                      <BetSelectionsList
                        isExoticBet={isExoticBet}
                        selectionsList={fullSelections.map((runnerSelections) =>
                          runnerSelections.filter((runner) => !runner.scratched)
                        )}
                        betTypeNames={betTypeNames}
                        isLeg={legCount > 1}
                      />
                    )}
                  </BetSelectionsContainer>
                </AnimatedSelectionsContainer>
              )}
            </LeftColumn>
            <RightColumn>
              <BetAmountSelector
                selectedAmount={selectedAmount}
                values={
                  customAmount
                    ? [...(wagerAmounts || []), customAmount, "Custom..."]
                    : [...(wagerAmounts || []), "Custom..."]
                }
                onSelect={(value) => {
                  if (value === "Custom...") {
                    toggleCustomAmountModal(true);
                  } else {
                    onSelectAmount(value);
                  }
                }}
              />
              {showVerifyButton ? (
                <VerifyButton />
              ) : (
                <>
                  {+userBalance >= +totalAmount ||
                  !isLogged ||
                  isLoading ||
                  isGeoComplyBetDisabled ? (
                    <PlaceBetButton
                      onClickPlaceBet={() => {
                        onPlaceBet(+totalAmount);
                        onPlaceBetGtm(totalAmount);
                      }}
                      placeAmount={+totalAmount}
                      remainingSelections={remainingSelections}
                      approxPayout={approxPayout ?? "0"}
                      isLoading={isLoading}
                      isLeg={legCount > 1}
                      isDisabled={
                        !isLogged
                          ? false
                          : isGeoComplyBetDisabled || isBettingDisabled
                      }
                    />
                  ) : (
                    <InsufficientFundsButton
                      onClickInsufficientFunds={onClickInsufficientFunds}
                      placeAmount={+totalAmount}
                    />
                  )}
                </>
              )}
            </RightColumn>
          </BetSlipWrapper>
        </BetSlipContainer>
      </AnimatedContainer>
    </>
  );
};

export default BetSlip;
